import type {ReactElement} from 'react';
import type {Editor} from 'grapesjs';
import {useContext, useState} from 'react';
import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal, closeModal} from '@Components/modal-container/modal-container-reducer';
import {EmailMakerBottomSheet} from '@EmailMakerReact/components/email-maker-bottom-sheet';
import {useCloseSheetOnCanvasInteraction} from '@EmailMakerReact/hooks/use-close-sheet-on-canvas-interaction';
import {useIsVersionHistoryAccessible} from '@EmailMakerReact/hooks/use-is-version-history-accessible';
import {Panel, PanelHeader} from '@Components/panel';
import type {PanelHeaderProps} from '@Components/panel';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {isUserLoggedIn} from '@Libraries/user.library';
import {shareEmailMarketingCampaignInTeam, trackGA4NumberOfSectionsInEmailCampaign} from '@Libraries/email-campaigns-library';
import {openEmailCampaignSendingOptionsModal} from '@Modals/email-campaign-sending-options-modal';
import {EmailCampaignSendingOptionsPanelTypes} from '@Panels/email-campaign-sending-options-panel/email-campaign-sending-options-panel.types';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {selectEmailMakerIsSharedInTeam, setEmailMakerIsSharedInTeam} from '@EmailMakerReact/reducers/email-maker-publish-state-reducer';
import {openEmailMakerPreviewModal} from '@EmailMakerReact/lib/email-maker-preview-library';
import {copyEmailHtmlAsTextForExport, copyEmailHtmlForExport, downloadHtmlForMarketingEmail, recordEmailExport} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {authenticateUser} from '@EmailMakerReact/lib/email-maker-publish-library';
import {getCurrentIdEmailCampaign, getCurrentIdEmailTemplate, resolveEmailCampaign} from '@EmailMakerReact/lib/email-maker-storage-library';
import {EmailMakerExportOption} from '@EmailMakerReact/email-maker-react.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {FILE_MENU_HELP_CENTER_URL, FILE_MENU_NEW_URL, FILE_MENU_SETTINGS_URL} from '@EmailMakerReact/layout/email-maker-nav/components/file-menu/file-menu.types';
import {EXPORT_STATE_RESET_DELAY_MS, ExportItemState} from '@EmailMakerReact/layout/email-maker-nav/components/export-menu/export-menu.types';
import type {EmailMakerMoreOptionsSheetProps, MoreOptionsMenuConfig} from './email-maker-more-options-sheet.types';
import {isMoreOptionsItem, MoreOptionsItem, MoreOptionsView} from './email-maker-more-options-sheet.types';
import styles from './email-maker-more-options-sheet.module.scss';

const SHEET_MODAL_ID = MODALS_IDS.EMAIL_MAKER_MORE_OPTIONS_MODAL;
const SHEET_PANEL_ID = 'email-maker-more-options-sheet-panel';

/** Must dispatch before the sheet's Modal mounts so the modalsHashmap entry exists when the Modal writes its dimensions. */
// eslint-disable-next-line react-refresh/only-export-components
export function openEmailMakerMoreOptionsSheet(): void {
  window.PMW.redux.store.dispatch(openPanelInModal({modalId: SHEET_MODAL_ID, panelProps: {}}));
}

export function EmailMakerMoreOptionsSheet({editor}: EmailMakerMoreOptionsSheetProps): ReactElement | null {
  const isOpen = useAppSelector((s) => s.modals.modalsHashmap[SHEET_MODAL_ID]?.isOpen ?? false);

  if (!isOpen) {
    return null;
  }

  return <MountedSheet editor={editor} />;
}

function MountedSheet({editor}: EmailMakerMoreOptionsSheetProps): ReactElement {
  const {store} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();
  const isSharedInTeam = useAppSelector(selectEmailMakerIsSharedInTeam);
  const [copyState, setCopyState] = useState<ExportItemState>(ExportItemState.DEFAULT);
  const [isShareToggleLoading, setIsShareToggleLoading] = useState<boolean>(false);
  const [view, setView] = useState<MoreOptionsView>(MoreOptionsView.MAIN);

  const canShareInTeam = isUserLoggedIn() && store.doesTemplateBelongToUser && !!store.idEmailMarketingCampaign;
  const isVersionHistoryAccessible = useIsVersionHistoryAccessible();

  function handleClose(): void {
    dispatch(closeModal(SHEET_MODAL_ID));
  }

  // The canvas is an iframe, so a tap outside the sheet surfaces as a selection event that closes it.
  useCloseSheetOnCanvasInteraction(editor, SHEET_MODAL_ID);

  function trackGA4SectionsUsed(): void {
    const idEmailCampaign = getCurrentIdEmailCampaign(editor);
    if (idEmailCampaign) {
      trackGA4NumberOfSectionsInEmailCampaign(idEmailCampaign);
    }
  }

  function handleDuplicate(): void {
    const idEmailTemplate = getCurrentIdEmailTemplate(editor);
    if (idEmailTemplate) {
      window.open(window.PMW.util.site_url(`emailmaker/copy/${idEmailTemplate}`), '_blank', 'noopener');
    } else {
      window.alert(window.i18next.t('pmwjs_save_and_try_again'));
    }
  }

  function onShareInTeamToggle(): void {
    if (!store.idEmailMarketingCampaign) {
      return;
    }
    const idEmailMarketingCampaign = store.idEmailMarketingCampaign;
    const nextIsShared = !isSharedInTeam;
    setIsShareToggleLoading(true);
    void shareEmailMarketingCampaignInTeam(nextIsShared, idEmailMarketingCampaign)
      .then((): void => {
        dispatch(setEmailMakerIsSharedInTeam(nextIsShared));
      })
      .finally((): void => {
        setIsShareToggleLoading(false);
      });
  }

  function runExportCopy(): void {
    authenticateUser(editor, {
      cb: (): void => {
        trackGA4SectionsUsed();
        setCopyState(ExportItemState.PROCESSING);
        void copyEmailHtmlForExport(editor)
          .then((): void => {
            recordEmailExport(editor, EmailMakerExportOption.COPY_EMAIL);
            setCopyState(ExportItemState.SUCCESS);
            setTimeout((): void => {
              setCopyState(ExportItemState.DEFAULT);
            }, EXPORT_STATE_RESET_DELAY_MS);
          })
          .catch((err: unknown): void => {
            window.PMW.log('Export copy failed', err);
            setCopyState(ExportItemState.DEFAULT);
          });
      },
    });
  }

  function runExport(action: (editor: Editor) => Promise<unknown>, recordOption: EmailMakerExportOption): void {
    authenticateUser(editor, {
      cb: (): void => {
        trackGA4SectionsUsed();
        void action(editor)
          .then((): void => {
            recordEmailExport(editor, recordOption);
          })
          .catch((err: unknown): void => {
            window.PMW.log('Export action failed', err);
          });
      },
    });
  }

  function openSendingOptions(type: EmailCampaignSendingOptionsPanelTypes): void {
    handleClose();
    void openSendingOptionsModal(editor, type).catch((err: unknown): void => {
      window.PMW.log('Opening sending options failed', err);
    });
  }

  const itemHandlers: Partial<Record<MoreOptionsItem, () => void>> = {
    [MoreOptionsItem.POST_TO_SOCIAL]: (): void => {
      openSendingOptions(EmailCampaignSendingOptionsPanelTypes.SHARE_ON_SOCIAL_MEDIA);
    },
    [MoreOptionsItem.PUBLISH_AS_WEBPAGE]: (): void => {
      openSendingOptions(EmailCampaignSendingOptionsPanelTypes.SHARE_AS_WEBPAGE);
    },
    [MoreOptionsItem.PREVIEW]: (): void => {
      handleClose();
      openEmailMakerPreviewModal(editor);
    },
    [MoreOptionsItem.EXPORT]: (): void => {
      setView(MoreOptionsView.EXPORT);
    },
    [MoreOptionsItem.EXPORT_COPY]: runExportCopy,
    [MoreOptionsItem.EXPORT_COPY_AS_TEXT]: (): void => {
      runExport(copyEmailHtmlAsTextForExport, EmailMakerExportOption.COPY_HTML);
    },
    [MoreOptionsItem.EXPORT_DOWNLOAD]: (): void => {
      runExport(downloadHtmlForMarketingEmail, EmailMakerExportOption.DOWNLOAD_HTML);
    },
    [MoreOptionsItem.NEW_EMAIL]: (): void => {
      handleClose();
      navigateToSiteUrl(FILE_MENU_NEW_URL);
    },
    [MoreOptionsItem.DUPLICATE]: (): void => {
      handleClose();
      handleDuplicate();
    },
    [MoreOptionsItem.VERSION_HISTORY]: (): void => {
      handleClose();
      dispatch(openPanelInModal({modalId: MODALS_IDS.EMAIL_MAKER_VERSION_HISTORY_SHEET, panelProps: {}}));
    },
    [MoreOptionsItem.SETTINGS]: (): void => {
      handleClose();
      navigateToSiteUrl(FILE_MENU_SETTINGS_URL);
    },
    [MoreOptionsItem.HELP_CENTER]: (): void => {
      handleClose();
      window.open(FILE_MENU_HELP_CENTER_URL, '_blank', 'noopener');
    },
  };

  const onClick = (id?: string): void => {
    if (id !== undefined && isMoreOptionsItem(id)) {
      itemHandlers[id]?.();
    }
  };

  const isExportView = view === MoreOptionsView.EXPORT;
  const items = isExportView ? buildExportMenuItems(copyState) : buildMainMenuItems({canShareInTeam, isSharedInTeam, isShareToggleLoading, isVersionHistoryAccessible, onShareInTeamToggle});

  return (
    <EmailMakerBottomSheet modalId={SHEET_MODAL_ID} panelId={SHEET_PANEL_ID}>
      <Panel
        panelHeader={renderSheetHeader(isExportView, handleClose, (): void => {
          setView(MoreOptionsView.MAIN);
        })}
        panelContent={
          <div className={styles.body}>
            <ControlledList items={items} onClick={onClick} />
          </div>
        }
      />
    </EmailMakerBottomSheet>
  );
}

function renderSheetHeader(isExportView: boolean, onClose: () => void, onBack: () => void): ReactElement<PanelHeaderProps> {
  if (isExportView) {
    return <PanelHeader title={window.i18next.t('pmwjs_export')} showBackButton onBackButton={onBack} onClose={onClose} />;
  }
  return <PanelHeader title={window.i18next.t('pmwjs_more_options')} onClose={onClose} />;
}

async function openSendingOptionsModal(editor: Editor, type: EmailCampaignSendingOptionsPanelTypes): Promise<void> {
  const campaign = await resolveEmailCampaign(editor);

  if (!campaign) {
    return;
  }

  openEmailCampaignSendingOptionsModal({campaignId: campaign.id, type});
}

function renderShareToggleElement(isSharedInTeam: boolean, isLoading: boolean, onToggle: () => void): ReactElement {
  return <ToggleSwitch isChecked={isSharedInTeam} onChange={onToggle} size={ToggleSwitchV2Size.SMALL} isLoading={isLoading} />;
}

function navigateToSiteUrl(path: string): void {
  window.location.href = window.PMW.util.site_url(path);
}

function getCopyText(copyState: ExportItemState): string {
  if (copyState === ExportItemState.PROCESSING) {
    return window.i18next.t('pmwjs_copying');
  }
  if (copyState === ExportItemState.SUCCESS) {
    return window.i18next.t('pmwjs_copied');
  }
  return window.i18next.t('pmwjs_copy');
}

function buildMainMenuItems({canShareInTeam, isSharedInTeam, isShareToggleLoading, isVersionHistoryAccessible, onShareInTeamToggle}: MoreOptionsMenuConfig): ControlledListItemType[] {
  const publishGroup: ControlledListItemType[] = [
    {id: MoreOptionsItem.POST_TO_SOCIAL, icon: 'icon-social-media', text: window.i18next.t('pmwjs_post_to_social'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.PUBLISH_AS_WEBPAGE, icon: 'icon-link', text: window.i18next.t('pmwjs_publish_webpage'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.PREVIEW, icon: 'icon-eye', text: window.i18next.t('pmwjs_preview'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.EXPORT, icon: 'icon-sign-in', text: window.i18next.t('pmwjs_export'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
  ];

  if (canShareInTeam) {
    publishGroup.push({
      id: MoreOptionsItem.SHARE_IN_TEAM,
      icon: 'icon-team',
      text: window.i18next.t('pmwjs_share_in_team'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      customElement: renderShareToggleElement(isSharedInTeam, isShareToggleLoading, onShareInTeamToggle),
    });
  }

  return [
    ...publishGroup,
    {id: 'more-options-divider-1', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER},
    {id: MoreOptionsItem.NEW_EMAIL, icon: 'icon-start-from-scratch', text: window.i18next.t('pmwjs_create_new_email'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.DUPLICATE, icon: 'icon-copy', text: window.i18next.t('pmwjs_duplicate_email'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.VERSION_HISTORY, icon: 'icon-history', text: window.i18next.t('pmwjs_version_history'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7, isDisabled: !isVersionHistoryAccessible},
    {id: 'more-options-divider-2', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER},
    {id: MoreOptionsItem.SETTINGS, icon: 'icon-settings', text: window.i18next.t('pmwjs_settings'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.HELP_CENTER, icon: 'icon-help', text: window.i18next.t('pmwjs_help_center'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
  ];
}

function buildExportMenuItems(copyState: ExportItemState): ControlledListItemType[] {
  return [
    {id: MoreOptionsItem.EXPORT_COPY, icon: 'icon-copy', text: getCopyText(copyState), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.EXPORT_COPY_AS_TEXT, icon: 'icon-pencil-line', text: window.i18next.t('pmwjs_copy_html'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
    {id: MoreOptionsItem.EXPORT_DOWNLOAD, icon: 'icon-sign-in', text: window.i18next.t('pmwjs_download'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7},
  ];
}
